import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  MapPin, 
  ExternalLink, 
  CheckCircle2, 
  Handshake,
  TrendingUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  X,
  Maximize2
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { infrastructureProjectsData } from '../data/mock';

const InfrastructureDetailPage = () => {
  const { projectId } = useParams();
  const { t, language } = useLanguage();
  const [scrollY, setScrollY] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const project = infrastructureProjectsData.find(p => p.id === projectId);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll reveal animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Lightbox functions
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const nextImage = () => {
    if (project.images && project.images.length > 0) {
      setLightboxIndex((prev) => (prev + 1) % project.images.length);
    }
  };

  const prevImage = () => {
    if (project.images && project.images.length > 0) {
      setLightboxIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
    }
  };

  if (!project) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-black">
        <div className="text-center">
          <h1 className="text-2xl font-light text-white mb-4">
            {language === 'en' ? 'Project Not Found' : language === 'ar' ? 'المشروع غير موجود' : 'پڕۆژە نەدۆزرایەوە'}
          </h1>
          <Link to="/projects" className="text-white/60 hover:text-white border-b border-white/50 pb-1">
            ← {language === 'en' ? 'Back to Projects' : language === 'ar' ? 'العودة إلى المشاريع' : 'گەڕانەوە بۆ پڕۆژەکان'}
          </Link>
        </div>
      </div>
    );
  }

  const getText = (field) => field[language] || field.en;

  // Different images for different sections
  const challengeImage = project.images?.[0] || project.heroImage;
  const partnershipImage = project.images?.[1] || project.heroImage;

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden" data-testid="infrastructure-detail-page">
      {/* Decorative Curved Lines */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden opacity-15">
        <svg className="absolute -left-1/4 top-0 h-full w-1/2" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M0,0 Q50,50 0,100" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.2" />
          <path d="M10,0 Q60,50 10,100" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.2" />
        </svg>
        <svg className="absolute -right-1/4 top-0 h-full w-1/2" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M100,0 Q50,50 100,100" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.2" />
          <path d="M90,0 Q40,50 90,100" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.2" />
        </svg>
      </div>

      {/* Hero Section - Full Viewport */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black z-10" />
        
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        >
          <source src="https://ik.imagekit.io/vdrhxyigu/0212.mp4" type="video/mp4" />
        </video>
        
        {/* Hero Content */}
        <div className="relative z-20 text-center px-6 max-w-5xl mx-auto">
          {/* Location */}
          <p className="text-sm sm:text-base uppercase tracking-[0.3em] text-white/70 mb-8 font-light animate-fade-in-up">
            {getText(project.location)}
          </p>
          
          {/* Project Title */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white tracking-tight mb-10 animate-fade-in-up delay-200">
            {getText(project.name)}
          </h1>
          
          {/* CTA Button */}
          <a
            href={project.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-luxury inline-flex items-center gap-3 px-10 py-4 border border-white/50 text-white text-xs uppercase tracking-[0.2em] font-light hover:bg-white hover:text-black transition-all duration-300 animate-fade-in-up delay-300"
          >
            <MapPin className="w-4 h-4" />
            {t('infrastructure.viewOnMap')}
          </a>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20">
          <div className="flex flex-col items-center gap-2 animate-bounce">
            <span className="text-xs uppercase tracking-widest text-white/40">
              {language === 'en' ? 'Scroll' : language === 'ar' ? 'تمرير' : 'سکرۆڵ'}
            </span>
            <ChevronDown className="w-5 h-5 text-white/40" />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="relative py-32 sm:py-40 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl text-center">
          <p className="text-lg sm:text-xl md:text-2xl text-white/80 font-light leading-relaxed scroll-reveal">
            {getText(project.overview)}
          </p>
        </div>
      </section>

      {/* The Challenge Section with Image */}
      <section className="relative py-32 bg-black overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Text Content */}
            <div className="order-2 lg:order-1 scroll-reveal-left">
              <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-6 font-light">
                {language === 'en' ? 'THE CHALLENGE' : language === 'ar' ? 'التحدي' : 'قەیرانەکە'}
              </p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight mb-8 leading-tight">
                {getText(project.name)}
              </h2>
              <p className="text-base sm:text-lg text-white/70 font-light leading-relaxed mb-8">
                {getText(project.challenge)}
              </p>
              <a
                href={project.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 text-white text-xs uppercase tracking-[0.2em] font-light border-b border-white/50 pb-2 hover:border-white transition-colors"
              >
                {t('infrastructure.viewOnMap')}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            
            {/* Image */}
            <div className="order-1 lg:order-2 scroll-reveal-right">
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={challengeImage}
                  alt={getText(project.name)}
                  className="w-full h-full object-cover"
                />
                <div className="absolute -bottom-6 -right-6 w-32 h-32 border border-white/20" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Objectives - Clean List Design */}
      <section className="relative py-32 bg-[#0a0a0a]">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          {/* Section Header */}
          <div className="text-center mb-20 scroll-reveal">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4 font-light">
              {language === 'en' ? 'KEY OBJECTIVES' : language === 'ar' ? 'الأهداف الرئيسية' : 'ئامانجە سەرەکییەکان'}
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight">
              {language === 'en' ? 'Project Goals' : language === 'ar' ? 'أهداف المشروع' : 'ئامانجەکانی پڕۆژە'}
            </h2>
          </div>

          {/* Objectives List */}
          <div className="space-y-6">
            {(project.objectives[language] || project.objectives.en).map((objective, index) => (
              <div 
                key={index}
                className="scroll-reveal flex items-start gap-8 p-8 border-l-2 border-white/20 hover:border-white/60 transition-colors bg-black/30"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <span className="text-4xl font-light text-white/20 flex-shrink-0">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <p className="text-lg text-white/80 font-light leading-relaxed pt-2">
                  {objective}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section - Scroll Reveal Style */}
      <section className="relative py-32 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <div className="text-center mb-20 scroll-reveal">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4 font-light">
              {t('infrastructure.projectTimeline')}
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight">
              {language === 'en' ? 'Our Journey' : language === 'ar' ? 'رحلتنا' : 'ڕێگاکەمان'}
            </h2>
          </div>

          {/* Timeline Items */}
          <div className="space-y-0">
            {project.timeline.map((item, index) => (
              <div 
                key={index} 
                className="scroll-reveal relative flex gap-8 lg:gap-16 pb-16"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                {/* Phase Number */}
                <div className="flex-shrink-0 w-24 lg:w-32 text-right">
                  <span className="text-4xl lg:text-5xl font-light text-white/20">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
                
                {/* Timeline Line */}
                <div className="relative flex-shrink-0">
                  <div className={`absolute top-3 left-1 w-px bg-white/20 ${index === project.timeline.length - 1 ? 'h-0' : 'h-full'}`} />
                  <div className="w-3 h-3 bg-white relative z-10" />
                </div>
                
                {/* Content */}
                <div className="flex-1 pt-0">
                  <h3 className="text-xl lg:text-2xl font-light text-white mb-4 tracking-wide">
                    {getText(item.phase)}
                  </h3>
                  <p className="text-white/50 font-light leading-relaxed">
                    {getText(item.description)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Design & Implementation */}
      <section className="relative py-32 bg-[#0a0a0a]">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-6 font-light scroll-reveal">
            {t('infrastructure.designImplementation')}
          </p>
          <p className="text-lg sm:text-xl md:text-2xl text-white/80 font-light leading-relaxed scroll-reveal">
            {getText(project.implementation)}
          </p>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="relative py-32 bg-black overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <div className="scroll-reveal-left">
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={partnershipImage}
                  alt="Partnership"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            {/* Text Content */}
            <div className="scroll-reveal-right">
              <Handshake className="w-12 h-12 text-white/40 mb-8" />
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight mb-8 leading-tight">
                {t('infrastructure.partnership')}
              </h2>
              <p className="text-base sm:text-lg text-white/70 font-light leading-relaxed">
                {getText(project.partnership)}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="relative py-32 bg-white text-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="text-center mb-20 scroll-reveal">
            <TrendingUp className="w-12 h-12 mx-auto mb-6 opacity-30" />
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
              {t('infrastructure.results')}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {(project.results[language] || project.results.en).map((result, index) => (
              <div 
                key={index}
                className="flex items-start gap-6 p-8 border border-black/10 hover:border-black/30 transition-colors scroll-reveal-scale"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex-shrink-0 w-10 h-10 bg-black flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
                <p className="text-black/80 font-light leading-relaxed">{result}</p>
              </div>
            ))}
          </div>

          {/* Conclusion */}
          <div className="mt-16 text-center scroll-reveal">
            <p className="text-lg sm:text-xl text-black/60 font-light italic max-w-3xl mx-auto">
              &ldquo;{getText(project.conclusion)}&rdquo;
            </p>
          </div>
        </div>
      </section>

      {/* Project Gallery Section */}
      {project.images && project.images.length > 0 && (
        <section className="relative py-32 bg-black">
          <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
            <div className="text-center mb-16 scroll-reveal">
              <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4 font-light">
                {t('infrastructure.projectGallery')}
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
                {language === 'en' ? 'Project Gallery' : language === 'ar' ? 'معرض المشروع' : 'گالەریی پڕۆژە'}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {project.images.map((image, index) => (
                <div 
                  key={index}
                  onClick={() => openLightbox(index)}
                  className="group relative aspect-video overflow-hidden cursor-pointer scroll-reveal-scale"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 z-10 transition-all duration-500" />
                  <img
                    src={image}
                    alt={`Project ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 z-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="w-14 h-14 border border-white flex items-center justify-center">
                      <Maximize2 className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="relative py-32 bg-[#0a0a0a]">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-6 font-light scroll-reveal">
            {language === 'en' ? 'GET IN TOUCH' : language === 'ar' ? 'تواصل معنا' : 'پەیوەندیمان پێوە بکە'}
          </p>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight mb-10 scroll-reveal">
            {t('cta.title')}
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 scroll-reveal">
            <Link
              to="/contact"
              className="btn-luxury px-12 py-5 bg-white text-black text-xs uppercase tracking-[0.2em] font-medium hover:bg-white/90"
            >
              {t('cta.button')}
            </Link>
            <Link
              to="/projects"
              className="btn-luxury px-12 py-5 border border-white/30 text-white text-xs uppercase tracking-[0.2em] font-light hover:bg-white hover:text-black transition-all"
            >
              {language === 'en' ? 'View All Projects' : language === 'ar' ? 'عرض جميع المشاريع' : 'هەموو پڕۆژەکان ببینە'}
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && project.images && project.images.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-14 h-14 border border-white/30 flex items-center justify-center hover:bg-white/10 transition-all z-50"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          <button
            onClick={prevImage}
            className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 border border-white/30 flex items-center justify-center hover:bg-white/10 transition-all z-50"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>

          <img
            src={project.images[lightboxIndex]}
            alt="Lightbox"
            className="max-w-[90vw] max-h-[90vh] object-contain"
          />

          <button
            onClick={nextImage}
            className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 border border-white/30 flex items-center justify-center hover:bg-white/10 transition-all z-50"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-xs uppercase tracking-widest">
            {lightboxIndex + 1} / {project.images.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default InfrastructureDetailPage;
